<template>
    <div :class="containerClasses">
        <b-row align-v="end" align-h="center">
            <b-col v-if="titles.save" cols="3">
                <n-button type="success" :disabled="isSaveDisabled" block @click="handleSave">
                    {{ titles.save }}
                </n-button>
            </b-col>

            <b-col v-if="titles.new" cols="3">
                <n-button type="info" :disabled="isNewDisabled" block @click="handleNew">
                    {{ titles.new }}
                </n-button>
            </b-col>

            <b-col v-if="titles.clear" cols="3">
                <n-button type="danger" :disabled="isClearDisabled" block @click="handleClear">
                    {{ titles.clear }}
                </n-button>
            </b-col>

            <b-col v-if="titles.cancel" cols="3">
                <n-button type="dark" :disabled="disableCancel" block @click="handleCancel">
                    {{ titles.cancel }}
                </n-button>
            </b-col>
        </b-row>
    </div>
</template>

<script lang="ts">
import { Component, Emit, Prop, Vue } from 'vue-property-decorator';
import { ButtonTitles, DEFAULT_TITLES } from "@/components/ButtonTitles";

const DEFAULT_CLEAR_MESSAGE = 'Are you sure you want to clear?';

@Component({
    name: 'NewClearSaveButtons',
})
export default class NewClearSaveButtons extends Vue {
    @Prop({ default: () => DEFAULT_TITLES })
    readonly titles!: ButtonTitles;

    @Prop({ type: Boolean, default: false })
    readonly sticky!: boolean;

    @Prop({ type: Boolean, default: null })
    readonly disable!: boolean;

    @Prop({ type: Boolean, default: false })
    readonly disableNew!: boolean;

    @Prop({ type: Boolean, default: true })
    readonly disableClear!: boolean;

    @Prop({ type: Boolean, default: true })
    readonly disableSave!: boolean;

    @Prop({ type: Boolean, default: false })
    readonly disableCancel!: boolean;

    @Prop({ type: Boolean, default: false })
    readonly showClearModal!: boolean;

    @Prop({ type: String, default: DEFAULT_CLEAR_MESSAGE })
    readonly clearModalMessage!: string;

    @Prop({ type: Boolean, default: false })
    readonly disableSaveAfterClick!: boolean;

    private saveWasClicked = false;

    get containerClasses(): Record<string, boolean> {
        return {
            'container py-3': !this.sticky,
            'sticky glass-pane': this.sticky
        };
    }

    get isNewDisabled(): boolean {
        if (this.disable != null) {
            return !this.disable;
        }
        return this.disableNew;
    }

    get isClearDisabled(): boolean {
        return this.disable || this.disableClear;
    }

    get isSaveDisabled(): boolean {
        if (this.saveWasClicked && this.disableSaveAfterClick) {
            return true;
        }
        return this.disable || this.disableSave;
    }

    @Emit('save-clicked')
    handleSave(): void {
        if (this.disableSaveAfterClick && !this.isSaveDisabled) {
            this.saveWasClicked = true;
        }
    }

    public resetSaveState(): void {
        this.saveWasClicked = false;
    }

    @Emit('new-clicked')
    handleNew(): void {
        // Event emitted
    }

    @Emit('cancel-clicked')
    handleCancel(): void {
        // Event emitted
    }

    async handleClear(): Promise<void> {
        if (!this.showClearModal) {
            this.$emit('clear-clicked');
            return;
        }

        try {
            const confirmed = await this.$bvModal.msgBoxConfirm(this.clearModalMessage, {
                title: 'Confirm',
                size: 'sm',
                buttonSize: 'sm',
                okVariant: 'danger',
                okTitle: 'YES',
                cancelTitle: 'NO',
                footerClass: 'p-2',
                hideHeaderClose: false,
                centered: true
            });

            if (!confirmed) return;
            this.$emit('clear-clicked');
        } catch (error) {
            console.error('Error in clear confirmation:', error);
        }
    }
}
</script>

<style scoped lang="scss">
.sticky {
    position: sticky;
    bottom: 0;
    z-index: 100;
    border-top: 1px solid rgba(0, 0, 0, 0.05);
    box-shadow: inset 0px 7px 6px -6px rgba(0, 0, 0, 0.1);
    margin: 30px -15px -10px -15px;
    padding: 4px 0;
}
</style>
